import React, { useState } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Domain {
  icon: string;
  label: string;
}

interface Trait {
  icon: string;
  title: string;
  desc: string;
}

interface TabItem {
  key: string;
  label: string;
}

// ── Data ───────────────────────────────────────────────────────────────────────

const DOMAINS: Domain[] = [
  { icon: '🛒', label: 'E-Commerce' },
  { icon: '📈', label: 'Trading Platforms' },
  { icon: '👥', label: 'HRMS' },
  { icon: '💬', label: 'Chat Systems' },
  { icon: '📞', label: 'VOIP Services' },
  { icon: '🏢', label: 'Enterprise Apps' },
];

const TRAITS: Trait[] = [
  {
    icon: '🏛',
    title: 'System Architect',
    desc: 'Designs scalable microservices, event-driven pipelines, and cloud infrastructure from the ground up.',
  },
  {
    icon: '🎯',
    title: 'Technical Leader',
    desc: 'Mentors engineers, drives code quality standards, and owns end-to-end technical decisions.',
  },
  {
    icon: '⚡',
    title: 'Performance Obsessed',
    desc: 'Optimises for sub-200ms APIs, >95% cache hit rates, and zero-downtime deployments.',
  },
  {
    icon: '🔒',
    title: 'Security-First',
    desc: 'Builds with RBAC, JWT/OAuth2, OWASP practices, and secrets management baked in from day one.',
  },
];

const TABS: TabItem[] = [
  { key: 'about',      label: 'About'      },
  { key: 'expertise',  label: 'Expertise'  },
  { key: 'traits',     label: 'Leadership' },
];

// ── Component ──────────────────────────────────────────────────────────────────

const AboutMe: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('about');

  return (
    <section id="about-me" className="relative py-28 bg-[#0d0d14] overflow-hidden">

      {/* Subtle bg glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto px-6">

        {/* ── Section label ── */}
        <div className="mb-14">
          <p className="text-emerald-400 font-mono text-sm tracking-widest uppercase mb-3">
            Who I Am
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white">About Me</h2>
        </div>

        {/* ── Two-column layout ── */}
        <div className="grid lg:grid-cols-[340px_1fr] gap-12 items-start">

          {/* ── LEFT — photo card ── */}
          <div className="relative">
            {/* Photo frame */}
            <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-[#13131e] group">
              {/* Accent corner */}
              <div
                className="absolute top-0 left-0 w-16 h-16 pointer-events-none z-10"
                style={{
                  background:
                    'linear-gradient(135deg, #10b98130 0%, transparent 60%)',
                }}
              />
              <div
                className="absolute bottom-0 right-0 w-16 h-16 pointer-events-none z-10"
                style={{
                  background:
                    'linear-gradient(315deg, #06b6d430 0%, transparent 60%)',
                }}
              />

              {/* Photo */}
              <img
                src="assets/images/about/piyushpic.jpeg"
                alt="Piyush Prajapati — Senior Full Stack Architect"
                className="w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                style={{ aspectRatio: '3 / 4', maxHeight: 420 }}
                onError={(e) => {
                  // Graceful fallback if image path isn't resolved
                  const t = e.currentTarget;
                  t.style.display = 'none';
                  const fallback = t.nextElementSibling as HTMLElement | null;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              {/* Fallback avatar (hidden by default) */}
              <div
                className="w-full items-center justify-center bg-[#0e0e1a] border-b border-slate-800"
                style={{ aspectRatio: '3 / 4', maxHeight: 420, display: 'none' }}
              >
                <div className="text-7xl select-none">👨‍💻</div>
              </div>

              {/* Name strip at bottom of photo */}
              <div className="px-5 py-4 bg-[#0e0e1a] border-t border-slate-800">
                <div className="font-black text-white text-lg leading-tight">Piyush Prajapati</div>
                <div className="text-emerald-400 text-sm font-mono mt-0.5">
                  Senior Full Stack Architect
                </div>
              </div>
            </div>

            {/* ── Status card ── */}
            <div className="mt-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
              <div>
                <div className="text-emerald-300 text-xs font-bold">Open to Opportunities</div>
                <div className="text-slate-500 text-xs font-mono mt-0.5">
                  Senior Architect / Lead roles
                </div>
              </div>
            </div>

            {/* ── Quick stats ── */}
            <div className="mt-4 grid grid-cols-3 gap-3">
              {[
                { value: '9.5+', label: 'Years' },
                { value: '75+', label: 'Projects' },
                { value: '6+', label: 'Domains' },
              ].map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col items-center py-3 rounded-xl bg-[#13131e] border border-slate-800"
                >
                  <span className="text-xl font-black text-emerald-400 leading-none">{s.value}</span>
                  <span className="text-xs text-slate-500 font-mono mt-1">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT — content ── */}
          <div>

            {/* Tab bar */}
            <div className="flex gap-1 mb-8 bg-[#0a0a12] rounded-xl p-1 border border-slate-800 w-fit">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    activeTab === tab.key
                      ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── TAB: About ── */}
            {activeTab === 'about' && (
              <div className="space-y-5">
                {/* Para 1 */}
                <p className="text-slate-300 leading-relaxed text-[15px]">
                  I am a{' '}
                  <span className="text-white font-bold">Senior Full Stack Architect</span> with{' '}
                  <span className="text-emerald-400 font-bold">9.5+ years of experience</span> in
                  designing and developing scalable, secure, and high-performance web applications.
                </p>

                {/* Para 2 — stack highlight */}
                <div className="p-4 rounded-xl bg-[#13131e] border border-slate-800">
                  <p className="text-slate-300 leading-relaxed text-[15px]">
                    I specialise in the{' '}
                    <span className="text-emerald-400 font-bold">MERN stack</span>{' '}
                    <span className="text-slate-500 text-sm">
                      (MongoDB, Express.js, React.js, Node.js)
                    </span>{' '}
                    along with strong expertise in{' '}
                    <span className="text-white font-semibold">Next.js</span>,{' '}
                    <span className="text-white font-semibold">TypeScript</span>, and backend
                    frameworks like{' '}
                    <span className="text-white font-semibold">NestJS</span> and{' '}
                    <span className="text-white font-semibold">FastAPI</span>.
                  </p>
                </div>

                {/* Para 3 */}
                <p className="text-slate-300 leading-relaxed text-[15px]">
                  Beyond development, I focus on{' '}
                  <span className="text-white font-semibold">system design</span>,{' '}
                  <span className="text-white font-semibold">microservices architecture</span>, and{' '}
                  <span className="text-white font-semibold">cloud infrastructure</span> (AWS,
                  Docker) — ensuring applications are optimised for performance, scalability, and
                  reliability.
                </p>

                {/* Para 4 — domains */}
                <div>
                  <p className="text-slate-300 leading-relaxed text-[15px] mb-4">
                    I have worked across diverse domains — delivering production-grade solutions:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {DOMAINS.map((d) => (
                      <span
                        key={d.label}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#13131e] border border-slate-800 hover:border-slate-600 text-sm text-slate-300 font-medium transition-colors duration-200 cursor-default"
                      >
                        <span>{d.icon}</span>
                        {d.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Para 5 */}
                <p className="text-slate-300 leading-relaxed text-[15px]">
                  I also{' '}
                  <span className="text-white font-semibold">mentor developers</span>, lead
                  technical decisions, and ensure best practices in code quality, security, and
                  system architecture.
                </p>

                {/* Seeking banner */}
                <div
                  className="flex items-start gap-4 p-5 rounded-xl border"
                  style={{
                    background: 'linear-gradient(135deg, #10b98110 0%, #06b6d408 100%)',
                    borderColor: '#10b98130',
                  }}
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shrink-0 mt-1" />
                  <div>
                    <div className="text-white font-bold text-sm mb-1">Currently Seeking</div>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      Senior architect-level opportunities where I can drive technical vision
                      and build scalable products.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── TAB: Expertise ── */}
            {activeTab === 'expertise' && (
              <div className="space-y-6">
                {/* Stack groups */}
                {[
                  {
                    category: 'Frontend',
                    color: '#06b6d4',
                    items: ['React.js', 'Next.js', 'TypeScript', 'Angular', 'Tailwind CSS'],
                  },
                  {
                    category: 'Backend',
                    color: '#10b981',
                    items: ['Node.js', 'Express.js', 'NestJS', 'FastAPI', 'PHP / CodeIgniter'],
                  },
                  {
                    category: 'Databases',
                    color: '#8b5cf6',
                    items: ['MongoDB', 'MySQL', 'PostgreSQL', 'Redis'],
                  },
                  {
                    category: 'Architecture & Cloud',
                    color: '#f59e0b',
                    items: ['Microservices', 'System Design', 'AWS (EC2, S3, CloudFront)', 'Docker', 'REST / WebSocket APIs'],
                  },
                  {
                    category: 'Practices',
                    color: '#ec4899',
                    items: ['RBAC & OAuth2', 'CI/CD Pipelines', 'Code Review & Mentoring', 'Performance Optimisation'],
                  },
                ].map((group) => (
                  <div key={group.category}>
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ background: group.color }}
                      />
                      <span
                        className="text-xs font-mono uppercase tracking-widest"
                        style={{ color: group.color }}
                      >
                        {group.category}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {group.items.map((item) => (
                        <span
                          key={item}
                          className="px-3 py-1.5 text-sm rounded-lg bg-[#13131e] border border-slate-800 hover:border-slate-600 text-slate-300 font-medium transition-colors duration-200 cursor-default"
                          style={{
                            borderLeftColor: `${group.color}50`,
                            borderLeftWidth: 2,
                          }}
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── TAB: Leadership ── */}
            {activeTab === 'traits' && (
              <div className="grid sm:grid-cols-2 gap-4">
                {TRAITS.map((t, i) => (
                  <div
                    key={i}
                    className="group p-5 rounded-xl bg-[#13131e] border border-slate-800 hover:border-emerald-500/30 transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{t.icon}</span>
                      <span className="font-bold text-white text-sm group-hover:text-emerald-100 transition-colors">
                        {t.title}
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">{t.desc}</p>
                  </div>
                ))}

                {/* Education quick-view */}
                <div className="sm:col-span-2 mt-2 p-5 rounded-xl bg-[#13131e] border border-slate-800">
                  <div className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-4">
                    Education
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      {
                        degree: 'MCA',
                        full: 'Master of Computer Application',
                        institute: 'Kadi University, Gandhinagar',
                        year: '2015',
                        score: '7.22 CPI',
                      },
                      {
                        degree: 'BCA',
                        full: 'Bachelor of Computer Application',
                        institute: 'HNGU University, Kadi',
                        year: '2012',
                        score: '60%',
                      },
                    ].map((edu) => (
                      <div key={edu.degree} className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center text-xs font-black font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        >
                          {edu.degree}
                        </div>
                        <div>
                          <div className="font-bold text-white text-sm">{edu.full}</div>
                          <div className="text-slate-400 text-xs mt-0.5">{edu.institute}</div>
                          <div className="text-slate-600 text-xs font-mono mt-0.5">
                            {edu.year} · {edu.score}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutMe;
